/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './modal/Modal';
import { Button } from '../shared/ui';
import { buildGoogleMapsNavigationUrl, buildWazeNavigationUrl } from '../pages/homePage/mapPortSearchUtils';
import { buildMapStationShareUrl } from '../pages/homePage/mapFilterUrl';
import { cleanEvuaDisplayText, loadEvuaStationDetails } from '../pages/homePage/evuaStationDetails';
import { evuaAcdcForMapFilters } from '../pages/homePage/evuaMapFilters';
import { shareViaApps } from '../utils/shareViaApps';
import OutageScheduleButton from './outageSchedule/OutageScheduleButton';
import styles from './OpenMapsConfirmModal.module.css';

function uniqueTitleParts(values) {
  const seen = new Set();
  const parts = [];
  for (const value of values) {
    const trimmed = cleanEvuaDisplayText(value);
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    parts.push(trimmed);
  }
  return parts;
}

function EvuaModalTitle({ details, showEvua }) {
  const { t } = useTranslation();
  const parts = showEvua ? uniqueTitleParts([details?.operator, details?.vendor, details?.name]) : [];
  const title = parts.length ? parts.join(' · ') : t('openMapsTitle');

  return (
    <h2 id="open-maps-confirm-title" className={styles.title}>
      {title}
    </h2>
  );
}

function EvuaStationDetailsPanel({ details, loading }) {
  const { t } = useTranslation();

  if (!details && !loading) {
    return <p className={styles.detailsStatus}>{t('openMapsEvuaDetailsEmpty')}</p>;
  }

  if (!details) {
    return null;
  }

  if (!details.connectors?.length && !details.price && !details.power) {
    return null;
  }

  return (
    <div className={styles.details} aria-live="polite">
      <dl className={styles.detailsList}>
        {details.connectors?.length ? (
          <div className={styles.detailsRow}>
            <dt>{t('openMapsEvuaConnectors')}</dt>
            <dd>{details.connectors.join(', ')}</dd>
          </div>
        ) : null}
        {details.power ? (
          <div className={styles.detailsRow}>
            <dt>{t('openMapsEvuaPower')}</dt>
            <dd>{t('openMapsEvuaPowerValue', { power: details.power })}</dd>
          </div>
        ) : null}
        {details.price ? (
          <div className={styles.detailsRow}>
            <dt>{t('openMapsEvuaPrice')}</dt>
            <dd>{t('openMapsEvuaPriceValue', { price: details.price })}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

export default function OpenMapsConfirmModal({
  modalOpen,
  setModalOpen,
  latitude,
  longitude,
  stationNumber = null,
  evuaUrl = null,
  evuaPort = null,
  portFilter = 'all',
  stationKey = null,
}) {
  const { t } = useTranslation();
  const googleUrl = buildGoogleMapsNavigationUrl(latitude, longitude);
  const wazeUrl = buildWazeNavigationUrl(latitude, longitude);
  const showEvua = Boolean(evuaUrl || evuaPort);
  const [shareCopied, setShareCopied] = useState(false);
  const shareUrl = useMemo(() => {
    if (!stationKey || !modalOpen || typeof window === 'undefined') return '';
    return buildMapStationShareUrl(stationKey, window.location.search);
  }, [stationKey, modalOpen]);

  useEffect(() => {
    if (!modalOpen) setShareCopied(false);
  }, [modalOpen]);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const acdc = evuaAcdcForMapFilters({ portFilter });

  useEffect(() => {
    if (!modalOpen || !evuaPort) {
      setDetails(null);
      setLoading(false);
      return undefined;
    }

    const abort = new AbortController();
    let cancelled = false;
    setLoading(true);

    loadEvuaStationDetails(evuaPort, { signal: abort.signal, acdc })
      .then(result => {
        if (!cancelled) {
          setDetails(result?.details ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetails(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [modalOpen, evuaPort, acdc]);

  const openExternal = url => {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    setModalOpen(false);
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    setShareCopied(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch {
      // Native share below still carries the link.
    }
    const title = uniqueTitleParts([details?.operator, details?.vendor, details?.name]).join(' · ');
    await shareViaApps({
      title: title || t('openMapsTitle'),
      text: title || t('openMapsTitle'),
      url: shareUrl,
    });
  };

  return (
    <Modal modalOpen={modalOpen} setModalOpen={setModalOpen}>
      <div className={styles.container} role="dialog" aria-modal="true" aria-labelledby="open-maps-confirm-title">
        <EvuaModalTitle details={details} showEvua={showEvua} />
        {showEvua ? <EvuaStationDetailsPanel details={details} loading={loading} /> : null}
        <div className={styles.actions}>
          <Button type="button" className={styles.actionBtn} disabled={!wazeUrl} onClick={() => openExternal(wazeUrl)}>
            {t('openMapsInWaze')}
          </Button>
          <Button
            type="button"
            className={styles.actionBtn}
            disabled={!googleUrl}
            onClick={() => openExternal(googleUrl)}
          >
            {t('openMapsInGoogle')}
          </Button>
          {evuaUrl ? (
            <Button type="button" className={styles.actionBtn} onClick={() => openExternal(evuaUrl)}>
              {t('openMapsInEvua')}
            </Button>
          ) : null}
          {shareUrl ? (
            <Button type="button" variant="outline-simple" className={styles.actionBtn} onClick={handleShare}>
              {shareCopied ? t('b2bMarketplaceShareCopied') : t('b2bMarketplaceShareBtn')}
            </Button>
          ) : null}
          {stationKey ? (
            <OutageScheduleButton
              latitude={latitude}
              longitude={longitude}
              stationNumber={stationNumber}
              variant="outline-simple"
              className={styles.actionBtn}
              labelStyle={null}
            />
          ) : null}
          <Button
            type="button"
            variant="outline-simple"
            className={styles.actionBtn}
            onClick={() => setModalOpen(false)}
          >
            {t('close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
